import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Button, Grid, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import { fetchDepartments, createDepartment, updateDepartment, deleteDepartment } from '../../app/slices/departmentsSlice';
import { PageHeader } from '../../components/common/PageHeader';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

export default function DepartmentsPage() {
  const dispatch = useDispatch();
  const { list, loading } = useSelector(s => s.departments);
  const [dlg, setDlg]         = useState(false);
  const [editItem, setEditItem]= useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [form, setForm]       = useState({ name: '', description: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => { dispatch(fetchDepartments()); }, [dispatch]);

  const openCreate = () => { setForm({ name: '', description: '' }); setEditItem(null); setError(''); setDlg(true); };
  const openEdit   = d => { setForm({ name: d.name, description: d.description || '' }); setEditItem(d); setError(''); setDlg(true); };

  const handleSave = async () => {
    setSaving(true); setError('');
    const res = editItem
      ? await dispatch(updateDepartment({ id: editItem.id, dto: form }))
      : await dispatch(createDepartment(form));
    if (res.type.endsWith('/rejected')) setError(res.payload || 'Error.');
    else setDlg(false);
    setSaving(false);
  };

  const handleDelete = async () => {
    setSaving(true);
    await dispatch(deleteDepartment(deleteItem.id));
    setSaving(false); setDeleteItem(null);
  };

  if (loading && !list.length) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader title="Departments" subtitle={`${list.length} departments`}
        action={<Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Department</Button>} />
      <Grid container spacing={2.5}>
        {list.map(d => (
          <Grid item xs={12} sm={6} md={4} key={d.id}>
            <Card sx={{ p: 2.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box display="flex" gap={2.5} alignItems="center">
                  <Box sx={{ p: 1, bgcolor: 'rgba(99,102,241,0.12)', borderRadius: 2 }}>
                    <BusinessIcon sx={{ color: 'primary.light', fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>{d.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{d.userCount} users · {d.taskCount} tasks</Typography>
                  </Box>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteItem(d)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              </Box>
              {d.description && <Typography variant="body2" color="text.secondary" mt={1.5}>{d.description}</Typography>}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Department' : 'New Department'}</DialogTitle>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} fullWidth required />
          <TextField label="Description (optional)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} fullWidth multiline rows={2} />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>{saving ? 'Saving...' : editItem ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!deleteItem} title="Delete Department" message={`Delete "${deleteItem?.name}"? This cannot be undone.`}
        onConfirm={handleDelete} onCancel={() => setDeleteItem(null)} loading={saving} />
    </Box>
  );
}
